import { useTranslation } from "react-i18next";
import { Box, Typography, Chip } from "@mui/material";
import Grid from "@mui/material/Grid";
import { IContract } from "../../../../types/contract";
import { ContractExtractionResult } from "../../../../types/aiOutput";
import VerifiedAISuggestion from "./VerifiedAISuggestion";
import AISuggestion from "./AISuggestion";
import { useGetHumanDataVerificationsQuery } from "../../../../redux/slices/indexApiSlice";
import { DialogMode } from "../../../../types/dialogmode";
import { TreeItem2 } from "@mui/x-tree-view/TreeItem2";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { IDocument } from "../../../../types/document";

interface IContractSubjectProps {
  contract: IContract;
  mode: DialogMode;
  aiOutputs: ContractExtractionResult[];
  hideTitle?: boolean;
  documents: IDocument[];
}

const SubjectCard = ({
  subject,
  explanation,
}: {
  subject?: string;
  explanation?: string;
}) => {
  return (
    <Grid container size={12} gap={2}>
      {subject && <Typography variant="body1">{subject}</Typography>}

      {explanation && <Typography variant="body1">{explanation}</Typography>}
    </Grid>
  );
};

function ContractSubject({ ...props }: IContractSubjectProps) {
  const { contract, mode, aiOutputs, documents } = props;
  const { t } = useTranslation();

  const { data: verifications } = useGetHumanDataVerificationsQuery(contract);

  return (
    <Box pb={2}>
      {!props.hideTitle && (
        <Typography variant="h6" mb={2}>
          {t("Subject")}
        </Typography>
      )}

      <SubjectCard subject={contract?.subject} />

      {aiOutputs?.length > 0 && (
        <Box my={5}>
          <SimpleTreeView>
            {aiOutputs?.map((aiOutput) => {
              // if (aiOutput.id === latestAiOutput?.id) {
              //   // skip the latest ai output as it is already shown
              //   return null;
              // }

              const currentResult = aiOutput.metadata?.structuralData?.subject;

              if (!currentResult) {
                return null;
              }

              const verificationRecord = verifications?.find(
                (v) => v.field === "subject" && v.aiOutputId === aiOutput.id
              );

              const document = documents.find(
                (d) => d.id === aiOutput.documentId
              );

              return (
                <TreeItem2
                  itemId={`${aiOutput.id}-prompts`}
                  label={
                    <Typography>
                      {aiOutput.processedAt
                        ? t("date.dateTime", {
                            date: new Date(aiOutput.processedAt),
                          })
                        : "No processing date"}
                    </Typography>
                  }
                  style={{
                    marginBottom: 5,
                    gap: 5,
                  }}
                  slotProps={{
                    content: {
                      style: {
                        paddingTop: 10,
                        paddingBottom: 10,
                      },
                    },
                  }}
                >
                  <Box my={2} display="flex" flexDirection="column" gap={2}>
                    {document && documents?.length > 1 && (
                      <div>
                        <Chip
                          label={
                            document.type === "attachment"
                              ? document.name
                              : t("Main document")
                          }
                          color="primary"
                          size="small"
                          // icon={<AttachFile />}
                          variant="outlined"
                        />
                      </div>
                    )}

                    {verificationRecord && (
                      <VerifiedAISuggestion
                        verificationRecord={verificationRecord}
                      />
                    )}

                    <AISuggestion
                      contract={contract}
                      mode={mode}
                      isAiSuggestion={!verificationRecord}
                      collapsible={false}
                      isVerified={verificationRecord?.isCorrect !== undefined}
                      field="subject"
                      value={currentResult}
                      aiInputId={aiOutput.aiInputId}
                      aiOutputId={aiOutput.id}
                      content={<SubjectCard subject={currentResult} />}
                      processedAt={aiOutput?.processedAt}
                    />
                  </Box>
                </TreeItem2>
              );
            })}
          </SimpleTreeView>
        </Box>
      )}
    </Box>
  );
}

export default ContractSubject;
